import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Box,
    Typography,
    IconButton,
    Chip,
    Avatar,
    Alert,
    CircularProgress,
    Autocomplete,
    TextField,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    SportsEsports as SportsEsportsIcon,
    Person as PersonIcon,
    Deck as DeckIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    PlayArrow as PlayArrowIcon,
    Stop as StopIcon,
    LocalFireDepartment as LocalFireDepartmentIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
    getDecksWithArchetypes,
    getAllArchetypes,
    createMatch,
    updateMatch,
    updateUserLastSelectedDeck,
    updateUserLastSelectedArchetype
} from '../../services/tracker-limited-data';
import { getAllInkColors } from './InkColorIcon';

function QuickMatchForm({ open, onClose, preselectedDeckId = null, editMatch = null, onMatchAdded, trackerFilters = {} }) {
    const { currentUser, userData } = useAuth();
    const [formData, setFormData] = useState({
        myDeckId: '',
        opponentArchetype: null, // będzie to obiekt archetypu
        playDraw: '', // 'play' or 'draw'
        result: '', // 'win' or 'lose'
        comment: '', // opisowy komentarz do meczu
        tryhard: false
    });
    const [selectedColors, setSelectedColors] = useState([]); // wybrane kolory atramentu
    const [decks, setDecks] = useState([]);
    const [archetypes, setArchetypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const inkColors = getAllInkColors();

    // Ładowanie danych przy otwarciu formularza
    useEffect(() => {
        if (open && currentUser) {
            loadData();
        }
    }, [open, currentUser, trackerFilters]);

    // Wypełnij formularz danymi meczu w trybie edycji
    useEffect(() => {
        if (open && editMatch && decks.length > 0 && archetypes.length > 0) {
            const opponentArchetype = editMatch.opponentArchetypeId
                ? archetypes.find(a => a.id === editMatch.opponentArchetypeId) || null
                : null;
            setFormData({
                myDeckId: editMatch.myDeckId || '',
                opponentArchetype,
                playDraw: editMatch.playDraw || '',
                result: editMatch.result || '',
                comment: editMatch.comment || '',
                tryhard: Boolean(editMatch.tryhard)
            });
            if (opponentArchetype?.ink_colors?.length) {
                setSelectedColors(opponentArchetype.ink_colors);
            } else {
                setSelectedColors([]);
            }
        }
    }, [open, editMatch, decks.length, archetypes.length]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [userDecksData, internalDecksData, archetypesData] = await Promise.all([
                getDecksWithArchetypes(currentUser.uid, trackerFilters), // Talie użytkownika
                getDecksWithArchetypes(null, trackerFilters), // Talie wewnętrzne i publiczne
                getAllArchetypes(trackerFilters)
            ]);

            // Połącz talie użytkownika z wewnętrznymi, usuwając duplikaty
            const allDecks = [...userDecksData];
            const userDeckIds = new Set(userDecksData.map(deck => deck.id));

            internalDecksData.forEach(internalDeck => {
                if (!userDeckIds.has(internalDeck.id)) {
                    allDecks.push(internalDeck);
                }
            });

            setDecks(allDecks);
            setArchetypes(archetypesData);

            // Debug: sprawdź dane
            console.log('Ładowanie danych - decks z archetypami:', allDecks);
            console.log('Ładowanie danych - archetypes:', archetypesData);

            // Ustaw domyślną talię (pre-wybraną, ostatnio wybraną lub pierwszą dostępną) - tylko gdy nie edytujemy
            if (!editMatch) {
                const defaultDeckId = preselectedDeckId || userData?.lastSelectedDeckId || (allDecks.length > 0 ? allDecks[0].id : '');
                const defaultArchetype = userData?.lastSelectedArchetypeId
                    ? archetypesData.find(archetype => archetype.id === userData.lastSelectedArchetypeId) || null
                    : null;

                setFormData(prev => ({
                    ...prev,
                    myDeckId: defaultDeckId,
                    opponentArchetype: defaultArchetype
                }));
            }
        } catch (err) {
            console.error('Błąd podczas ładowania danych:', err);
            setError('Nie udało się załadować danych. Spróbuj ponownie.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleArchetypeChange = (event, newValue) => {
        setFormData(prev => ({
            ...prev,
            opponentArchetype: newValue
        }));
    };

    const handleButtonClick = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        if (!currentUser) {
            setError('Musisz być zalogowany, aby dodawać mecze.');
            return;
        }

        if (!formData.myDeckId || !formData.playDraw || !formData.result) {
            setError('Talia, Play/Draw i wynik są wymagane.');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);

            const matchData = {
                myDeckId: formData.myDeckId,
                opponentArchetypeId: formData.opponentArchetype?.id || null,
                playDraw: formData.playDraw,
                result: formData.result,
                comment: formData.comment || null,
                tryhard: Boolean(formData.tryhard)
            };

            if (editMatch) {
                await updateMatch(editMatch.id, matchData);
                setSuccess('Mecz został zaktualizowany.');
            } else {
                await createMatch({
                    ...matchData,
                    userId: currentUser.uid,
                    timestamp: new Date().toISOString()
                });
                // Reset formularza - zachowaj deck i archetyp, wyczyść tylko play/draw, wynik i komentarz
                setFormData(prev => ({
                    ...prev,
                    playDraw: '',
                    result: '',
                    comment: '',
                    tryhard: false
                }));
                setSuccess('Mecz został dodany pomyślnie!');
            }

            // Wywołaj callback do odświeżenia statystyk
            if (onMatchAdded) {
                onMatchAdded();
            }

            // Ukryj komunikat po 2 sekundach
            setTimeout(() => {
                setSuccess(null);
            }, 2000);

            // Zamknij formularz po edycji
            if (editMatch) {
                setTimeout(() => onClose(), 1500);
            }

            // Aktualizuj ostatnio wybrane ustawienia użytkownika w tle (nie blokuj interfejsu)
            try {
                const updatePromises = [
                    updateUserLastSelectedDeck(currentUser.uid, formData.myDeckId)
                ];

                // Aktualizuj archetyp tylko jeśli został wybrany
                if (formData.opponentArchetype?.id) {
                    updatePromises.push(updateUserLastSelectedArchetype(currentUser.uid, formData.opponentArchetype.id));
                }

                await Promise.all(updatePromises);
            } catch (updateError) {
                // Loguj błąd, ale nie pokazuj go użytkownikowi, bo mecz został już dodany
                console.warn('Nie udało się zaktualizować preferencji użytkownika:', updateError);
            }
        } catch (err) {
            console.error('Błąd podczas zapisywania meczu:', err);
            setError('Nie udało się zapisać meczu. Spróbuj ponownie.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        // Reset formularza - zachowaj ostatnio wybrane wartości (tylko przy dodawaniu nowego)
        if (!editMatch) {
            const defaultDeckId = userData?.lastSelectedDeckId || (decks.length > 0 ? decks[0].id : '');
            const defaultArchetype = userData?.lastSelectedArchetypeId
                ? archetypes.find(archetype => archetype.id === userData.lastSelectedArchetypeId) || null
                : null;

            setFormData({
                myDeckId: defaultDeckId,
                opponentArchetype: defaultArchetype,
                playDraw: '',
                result: '',
                comment: '',
                tryhard: false
            });
        }
        setSelectedColors([]);
        setError(null);
        onClose();
    };

    const getInkColor = (colorName) => {
        const color = inkColors.find(c => c.value === colorName);
        return color ? color.color : '#6b7280';
    };

    const getSelectedDeck = () => {
        return decks.find(deck => deck.id === formData.myDeckId);
    };

    const getSelectedArchetype = () => {
        return formData.opponentArchetype;
    };

    // Filtruj archetypy na podstawie wybranych kolorów
    const getFilteredArchetypes = () => {
        if (selectedColors.length === 0) {
            return archetypes;
        }

        return archetypes.filter(archetype => {
            if (!archetype.ink_colors || !Array.isArray(archetype.ink_colors)) {
                return false;
            }

            // Sprawdź czy archetyp zawiera wszystkie wybrane kolory
            return selectedColors.every(selectedColor =>
                archetype.ink_colors.includes(selectedColor)
            );
        });
    };

    // Obsługa wyboru kolorów
    const handleColorSelect = (colorValue) => {
        setSelectedColors(prev => {
            if (prev.includes(colorValue)) {
                // Usuń kolor
                const newColors = prev.filter(c => c !== colorValue);
                // Jeśli usunięto kolor, sprawdź czy wybrany archetyp nadal pasuje
                if (formData.opponentArchetype && newColors.length > 0) {
                    const stillMatches = newColors.every(selectedColor =>
                        formData.opponentArchetype.ink_colors &&
                        formData.opponentArchetype.ink_colors.includes(selectedColor)
                    );
                    if (!stillMatches) {
                        setFormData(prev => ({ ...prev, opponentArchetype: null }));
                    }
                } else if (newColors.length === 0) {
                    // Jeśli usunięto wszystkie kolory, wyczyść archetyp
                    setFormData(prev => ({ ...prev, opponentArchetype: null }));
                }
                return newColors;
            } else {
                // Dodaj kolor (maksymalnie 2)
                if (prev.length >= 2) {
                    return prev;
                }
                const newColors = [...prev, colorValue];

                // Sprawdź czy wybrany archetyp nadal pasuje
                if (formData.opponentArchetype) {
                    const stillMatches = newColors.every(selectedColor =>
                        formData.opponentArchetype.ink_colors &&
                        formData.opponentArchetype.ink_colors.includes(selectedColor)
                    );
                    if (!stillMatches) {
                        setFormData(prev => ({ ...prev, opponentArchetype: null }));
                    }
                }

                // Automatycznie wybierz pierwszy archetyp z przefiltrowanej listy
                setTimeout(() => {
                    const filteredArchetypes = archetypes.filter(archetype => {
                        if (!archetype.ink_colors || !Array.isArray(archetype.ink_colors)) {
                            return false;
                        }
                        return newColors.every(selectedColor =>
                            archetype.ink_colors.includes(selectedColor)
                        );
                    });

                    if (filteredArchetypes.length > 0 && !formData.opponentArchetype) {
                        setFormData(prev => ({
                            ...prev,
                            opponentArchetype: filteredArchetypes[0]
                        }));
                    }
                }, 0);

                return newColors;
            }
        });
    };

    if (!currentUser) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Alert severity="warning">
                        Musisz być zalogowany, aby dodawać mecze.
                    </Alert>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: 'rgba(17, 24, 39, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: '20px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1,
                borderBottom: '1px solid rgba(76, 29, 149, 0.3)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SportsEsportsIcon sx={{ color: '#7c3aed', mr: 1 }} />
                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                        {editMatch ? 'Edytuj mecz' : 'Dodaj mecz'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Tryhard: gra pod analizę (open hand, test teamowy itp.)">
                        <Button
                            size="small"
                            variant={formData.tryhard ? 'contained' : 'text'}
                            onClick={() => setFormData((prev) => ({ ...prev, tryhard: !prev.tryhard }))}
                            startIcon={<LocalFireDepartmentIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                minWidth: 0,
                                px: 1.2,
                                py: 0.45,
                                borderRadius: '999px',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: formData.tryhard ? 'white' : 'rgba(251, 146, 60, 0.95)',
                                background: formData.tryhard ? 'linear-gradient(135deg, #f97316, #ea580c)' : 'transparent',
                                '&:hover': {
                                    backgroundColor: formData.tryhard ? undefined : 'rgba(249, 115, 22, 0.12)'
                                }
                            }}
                        >
                            Tryhard
                        </Button>
                    </Tooltip>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: 'rgba(196, 181, 253, 0.7)',
                            '&:hover': {
                                color: 'white',
                                backgroundColor: 'rgba(76, 29, 149, 0.2)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#7c3aed' }} />
                    </Box>
                ) : (
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={3}>
                            {/* Mój deck */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Moja talii
                                    </InputLabel>
                                    <Select
                                        value={formData.myDeckId}
                                        onChange={handleInputChange('myDeckId')}
                                        label="Moja talii"
                                        startAdornment={<DeckIcon sx={{ color: 'rgba(196, 181, 253, 0.7)', mr: 1 }} />}
                                        sx={{
                                            color: 'white',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.5)'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.8)'
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#7c3aed'
                                            }
                                        }}
                                    >
                                        {decks.map((deck) => {
                                            // Użyj archetypeInfo z getDecksWithArchetypes
                                            const deckArchetype = deck.archetypeInfo;

                                            // Debug: sprawdź dane
                                            console.log('Deck:', deck.name, 'archetype ID:', deck.archetype, 'archetypeInfo:', deckArchetype);

                                            return (
                                                <MenuItem key={deck.id} value={deck.id} sx={{ py: 1.5 }}>
                                                    <Box sx={{ width: '100%' }}>
                                                        {/* Nazwa talii na górze */}
                                                        <Typography sx={{
                                                            color: 'white',
                                                            fontWeight: 500,
                                                            mb: 0.5,
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {deck.name}
                                                        </Typography>

                                                        {/* Badge z ikonami kolorów i nazwą archetypu */}
                                                        {deckArchetype && (
                                                            <Box sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                backgroundColor: 'rgba(124, 58, 237, 0.2)',
                                                                px: 0.5,
                                                                py: 0.2,
                                                                borderRadius: '4px',
                                                                border: '1px solid rgba(124, 58, 237, 0.3)',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {/* Ikony kolorów archetypu */}
                                                                {deckArchetype.ink_colors && deckArchetype.ink_colors.length > 0 && (
                                                                    <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                                        {deckArchetype.ink_colors.slice(0, 3).map((color, colorIndex) => {
                                                                            const colorData = inkColors.find(c => c.value === color);
                                                                            return (
                                                                                <Avatar
                                                                                    key={colorIndex}
                                                                                    src={colorData?.icon}
                                                                                    sx={{
                                                                                        width: 12,
                                                                                        height: 12,
                                                                                        backgroundColor: getInkColor(color),
                                                                                        border: `1px solid ${getInkColor(color)}`
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })}
                                                                        {deckArchetype.ink_colors.length > 3 && (
                                                                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)', fontSize: '0.6rem' }}>
                                                                                +{deckArchetype.ink_colors.length - 3}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                                {/* Nazwa archetypu */}
                                                                <Typography variant="caption" sx={{
                                                                    color: 'rgba(196, 181, 253, 0.7)',
                                                                    fontSize: '0.7rem'
                                                                }}>
                                                                    {deckArchetype.name}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {!deckArchetype && deck.archetype && (
                                                            /* Fallback: pokaż ID archetypu jeśli nie znaleziono */
                                                            <Typography variant="caption" sx={{
                                                                color: 'rgba(239, 68, 68, 0.7)',
                                                                fontSize: '0.7rem',
                                                                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                                                px: 0.5,
                                                                py: 0.2,
                                                                borderRadius: '4px',
                                                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {deck.archetype}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Wybór kolorów atramentu przeciwnika */}
                            <Grid item xs={12}>
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mb: 1.5 }}>
                                        Wybierz kolory atramentu przeciwnika (opcjonalnie):
                                    </Typography>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 1,
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        p: 1.5,
                                        backgroundColor: 'rgba(17, 24, 39, 0.2)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(76, 29, 149, 0.15)',
                                        minHeight: 60,
                                        alignItems: 'center'
                                    }}>
                                        {inkColors.map((color) => {
                                            const isSelected = selectedColors.includes(color.value);
                                            const isDisabled = !isSelected && selectedColors.length >= 2;

                                            return (
                                                <Tooltip key={color.value} title={color.label}>
                                                    <Avatar
                                                        src={color.icon}
                                                        onClick={() => handleColorSelect(color.value)}
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            backgroundColor: color.color,
                                                            border: `3px solid ${isSelected ? '#ffffff' : 'rgba(76, 29, 149, 0.3)'}`,
                                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                            opacity: isDisabled ? 0.3 : 1,
                                                            transition: 'all 0.3s ease',
                                                            filter: isSelected ? 'none' : 'grayscale(0.3)',
                                                            boxShadow: isSelected ? `0 0 0 2px ${color.color}, 0 0 15px ${color.color}80` : 'none',
                                                            transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                                                            '&:hover': {
                                                                boxShadow: isSelected ? `0 0 0 2px ${color.color}, 0 0 20px ${color.color}90` : `0 0 8px ${color.color}40`
                                                            }
                                                        }}
                                                    />
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Przeciwnik (archetyp) */}
                            <Grid item xs={12}>
                                <Autocomplete
                                    value={formData.opponentArchetype}
                                    onChange={handleArchetypeChange}
                                    options={getFilteredArchetypes()}
                                    getOptionLabel={(option) => option && option.name ? option.name : ''}
                                    isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Archetyp przeciwnika (opcjonalnie)"
                                            placeholder={selectedColors.length > 0 ? "Wybierz archetyp z dopasowanych kolorów..." : "Wybierz archetyp przeciwnika (opcjonalnie)..."}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <PersonIcon sx={{ color: 'rgba(196, 181, 253, 0.7)', mr: 1 }} />
                                                        {/* Ikony kolorów wybranego archetypu */}
                                                        {formData.opponentArchetype && formData.opponentArchetype.ink_colors && Array.isArray(formData.opponentArchetype.ink_colors) && formData.opponentArchetype.ink_colors.length > 0 && (
                                                            <Box sx={{ display: 'flex', gap: 0.3, mr: 1 }}>
                                                                {formData.opponentArchetype.ink_colors.map((color, index) => {
                                                                    if (!color) return null;
                                                                    const colorData = inkColors.find(c => c.value === color);
                                                                    return (
                                                                        <Avatar
                                                                            key={index}
                                                                            src={colorData?.icon}
                                                                            sx={{
                                                                                width: 16,
                                                                                height: 16,
                                                                                backgroundColor: getInkColor(color),
                                                                                border: `1px solid ${getInkColor(color)}`
                                                                            }}
                                                                        />
                                                                    );
                                                                })}
                                                            </Box>
                                                        )}
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                )
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: 'white',
                                                    '& fieldset': {
                                                        borderColor: 'rgba(76, 29, 149, 0.5)'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'rgba(76, 29, 149, 0.8)'
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#7c3aed'
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => {
                                        if (!option) return null;

                                        return (
                                            <Box component="li" {...props}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                    {/* Ikony kolorów po lewej */}
                                                    {option.ink_colors && Array.isArray(option.ink_colors) && option.ink_colors.length > 0 && (
                                                        <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                            {option.ink_colors.map((color, index) => {
                                                                if (!color) return null;
                                                                const colorData = inkColors.find(c => c.value === color);
                                                                return (
                                                                    <Avatar
                                                                        key={index}
                                                                        src={colorData?.icon}
                                                                        sx={{
                                                                            width: 16,
                                                                            height: 16,
                                                                            backgroundColor: getInkColor(color),
                                                                            border: `1px solid ${getInkColor(color)}`
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                    {/* Nazwa archetypu po prawej */}
                                                    <Typography sx={{ color: 'white', flex: 1 }}>
                                                        {option.name || 'Brak nazwy'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    }}
                                    noOptionsText={selectedColors.length > 0 ? "Brak archetypów z wybranymi kolorami" : "Brak archetypów"}
                                    loadingText="Ładowanie..."
                                    sx={{
                                        '& .MuiAutocomplete-popupIndicator': {
                                            color: 'rgba(196, 181, 253, 0.7)'
                                        },
                                        '& .MuiAutocomplete-clearIndicator': {
                                            color: 'rgba(196, 181, 253, 0.7)'
                                        }
                                    }}
                                    PaperComponent={({ children, ...other }) => (
                                        <Box
                                            {...other}
                                            sx={{
                                                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                                                backdropFilter: 'blur(20px)',
                                                border: '1px solid rgba(76, 29, 149, 0.3)',
                                                borderRadius: '12px',
                                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                                                '& .MuiAutocomplete-listbox': {
                                                    '& .MuiAutocomplete-option': {
                                                        color: 'white',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(76, 29, 149, 0.2)'
                                                        },
                                                        '&.Mui-focused': {
                                                            backgroundColor: 'rgba(76, 29, 149, 0.3)'
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            {children}
                                        </Box>
                                    )}
                                />
                            </Grid>

                            {/* Play / Draw */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant={formData.playDraw === 'play' ? 'contained' : 'outlined'}
                                        onClick={() => handleButtonClick('playDraw', 'play')}
                                        startIcon={<PlayArrowIcon />}
                                        sx={{
                                            flex: 1,
                                            background: formData.playDraw === 'play'
                                                ? 'linear-gradient(135deg, #7c3aed, #3b82f6)'
                                                : 'transparent',
                                            borderColor: 'rgba(76, 29, 149, 0.5)',
                                            color: formData.playDraw === 'play' ? 'white' : 'rgba(196, 181, 253, 0.7)',
                                            '&:hover': {
                                                borderColor: '#7c3aed',
                                                backgroundColor: formData.playDraw === 'play'
                                                    ? 'linear-gradient(135deg, #6d28d9, #2563eb)'
                                                    : 'rgba(76, 29, 149, 0.1)'
                                            }
                                        }}
                                    >
                                        Play
                                    </Button>
                                    <Button
                                        variant={formData.playDraw === 'draw' ? 'contained' : 'outlined'}
                                        onClick={() => handleButtonClick('playDraw', 'draw')}
                                        startIcon={<StopIcon />}
                                        sx={{
                                            flex: 1,
                                            background: formData.playDraw === 'draw'
                                                ? 'linear-gradient(135deg, #7c3aed, #3b82f6)'
                                                : 'transparent',
                                            borderColor: 'rgba(76, 29, 149, 0.5)',
                                            color: formData.playDraw === 'draw' ? 'white' : 'rgba(196, 181, 253, 0.7)',
                                            '&:hover': {
                                                borderColor: '#7c3aed',
                                                backgroundColor: formData.playDraw === 'draw'
                                                    ? 'linear-gradient(135deg, #6d28d9, #2563eb)'
                                                    : 'rgba(76, 29, 149, 0.1)'
                                            }
                                        }}
                                    >
                                        Draw
                                    </Button>
                                </Box>
                            </Grid>

                            {/* Win / Lose */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant={formData.result === 'win' ? 'contained' : 'outlined'}
                                        onClick={() => handleButtonClick('result', 'win')}
                                        startIcon={<CheckCircleIcon />}
                                        sx={{
                                            flex: 1,
                                            background: formData.result === 'win'
                                                ? 'linear-gradient(135deg, #10b981, #059669)'
                                                : 'transparent',
                                            borderColor: formData.result === 'win'
                                                ? '#10b981'
                                                : 'rgba(16, 185, 129, 0.5)',
                                            color: formData.result === 'win' ? 'white' : 'rgba(16, 185, 129, 0.7)',
                                            '&:hover': {
                                                borderColor: '#10b981',
                                                backgroundColor: formData.result === 'win'
                                                    ? 'linear-gradient(135deg, #059669, #047857)'
                                                    : 'rgba(16, 185, 129, 0.1)'
                                            }
                                        }}
                                    >
                                        Wygrana
                                    </Button>
                                    <Button
                                        variant={formData.result === 'lose' ? 'contained' : 'outlined'}
                                        onClick={() => handleButtonClick('result', 'lose')}
                                        startIcon={<CancelIcon />}
                                        sx={{
                                            flex: 1,
                                            background: formData.result === 'lose'
                                                ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                                                : 'transparent',
                                            borderColor: formData.result === 'lose'
                                                ? '#ef4444'
                                                : 'rgba(239, 68, 68, 0.5)',
                                            color: formData.result === 'lose' ? 'white' : 'rgba(239, 68, 68, 0.7)',
                                            '&:hover': {
                                                borderColor: '#ef4444',
                                                backgroundColor: formData.result === 'lose'
                                                    ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                                                    : 'rgba(239, 68, 68, 0.1)'
                                            }
                                        }}
                                    >
                                        Przegrana
                                    </Button>
                                </Box>
                            </Grid>

                            {/* Komentarz */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Komentarz (opcjonalnie)"
                                    placeholder="Dodaj opisowy komentarz do meczu..."
                                    value={formData.comment}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        comment: e.target.value
                                    }))}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: 'white',
                                            '& fieldset': {
                                                borderColor: 'rgba(76, 29, 149, 0.5)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(76, 29, 149, 0.8)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#7c3aed',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'rgba(196, 181, 253, 0.7)',
                                            '&.Mui-focused': {
                                                color: '#7c3aed',
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            '&::placeholder': {
                                                color: 'rgba(196, 181, 253, 0.5)',
                                                opacity: 1,
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 2 }}>
                <Button
                    onClick={handleClose}
                    disabled={submitting}
                    sx={{
                        color: 'rgba(196, 181, 253, 0.7)',
                        borderColor: 'rgba(76, 29, 149, 0.5)',
                        '&:hover': {
                            borderColor: 'rgba(76, 29, 149, 0.8)',
                            backgroundColor: 'rgba(76, 29, 149, 0.1)'
                        }
                    }}
                >
                    Anuluj
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!formData.myDeckId || !formData.playDraw || !formData.result || submitting}
                    sx={{
                        background: 'linear-gradient(135deg, #7c3aed, #3b82f6)',
                        borderRadius: '12px',
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #6d28d9, #2563eb)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(124, 58, 237, 0.4)'
                        },
                        '&:disabled': {
                            background: 'rgba(76, 29, 149, 0.3)',
                            color: 'rgba(196, 181, 253, 0.5)'
                        }
                    }}
                >
                    {submitting ? 'Zapisywanie...' : 'Zapisz Mecz'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default QuickMatchForm;
